import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material'
import {
  BusinessCenter as JobApplicationsIcon,
  Groups as NetworkingIcon,
  Email as EmailIcon,
  Chat as SmsIcon,
  Star as NorthStarIcon,
} from '@mui/icons-material'
import {
  Client,
  contactsApi,
  emailsApi,
  jobOpportunitiesApi,
  messagesApi,
} from '../../services/api'
import { isJobSearchEligible } from '../../state/clients'
import { useWebSocket } from '../../hooks/useWebSocket'

type CountKey = 'job-applications' | 'networking' | 'email' | 'sms'

/**
 * The client's records on other pages, with how many of each there are.
 *
 * The record page used to be a dead end: the only way out of it was the browser
 * back button, even though a client's applications, network, emails and texts
 * all live on pages that scope themselves to one client. This is the way out,
 * and the counts are what make it worth reading rather than clicking — you can
 * see there is nothing in Networking without going to look.
 *
 * It is deliberately not part of the section spine above it. That control
 * scrolls the record; this one leaves it, and one control that sometimes
 * scrolls and sometimes navigates is a control you cannot predict.
 */
export default function RelatedRecords({ client }: { client: Client }) {
  const navigate = useNavigate()
  const [counts, setCounts] = useState<Partial<Record<CountKey, number>>>({})

  // Four independent reads, each landing on its own: the row paints with its
  // links immediately and fills in as the counts arrive, because a link is
  // useful before its count is. A read that fails leaves that entry countless
  // rather than taking the row — or the record page — with it.
  useEffect(() => {
    let cancelled = false
    setCounts({})

    const record = (key: CountKey, read: Promise<unknown[]>) => {
      read
        .then((rows) => {
          if (!cancelled) setCounts((prev) => ({ ...prev, [key]: rows.length }))
        })
        .catch((err) => {
          console.warn(`Could not count ${key} for client ${client.id}:`, err)
        })
    }

    record('job-applications', jobOpportunitiesApi.list({ clientId: client.id }))
    record('networking', contactsApi.list(client.id))
    record('email', emailsApi.list(client.id))
    record('sms', messagesApi.list(client.id))

    return () => {
      cancelled = true
    }
  }, [client.id])

  // MWA-390 — the counts follow what happens to this client while the page is open: a
  // reply landing, a job added or removed, a text. Each event is checked against this
  // client, and only the count it affects is read again.
  const recount = useCallback(
    (key: CountKey) => {
      const reads: Record<CountKey, () => Promise<unknown[]>> = {
        'job-applications': () => jobOpportunitiesApi.list({ clientId: client.id }),
        networking: () => contactsApi.list(client.id),
        email: () => emailsApi.list(client.id),
        sms: () => messagesApi.list(client.id),
      }
      reads[key]()
        .then((rows) => setCounts((prev) => ({ ...prev, [key]: rows.length })))
        .catch((err) => console.warn(`Could not recount ${key} for client ${client.id}:`, err))
    },
    [client.id]
  )
  const liveHandlers = useMemo(() => {
    const forThisClient = (message: { data: unknown }) =>
      (message.data as { clientId?: string } | null)?.clientId === client.id
    const on = (key: CountKey) => (message: { data: unknown }) => {
      if (forThisClient(message)) recount(key)
    }
    return {
      email_received: on('email'),
      email_arrived: on('email'),
      email_sent: on('email'),
      sms_received: on('sms'),
      sms_sent: on('sms'),
      job_opportunity_created: on('job-applications'),
      job_opportunity_deleted: on('job-applications'),
      ws_reconnected: () => {
        ;(['job-applications', 'networking', 'email', 'sms'] as CountKey[]).forEach(recount)
      },
    }
  }, [client.id, recount])
  useWebSocket(liveHandlers)

  // The Email inbox has no per-client route, but its search matches the sender
  // address exactly, which is the same thing for a client who has one.
  const emailSearch = client.email || client.name || ''

  const entries: {
    key: string
    label: string
    Icon: typeof EmailIcon
    to: string
    count?: number
    disabledReason?: string
  }[] = [
    {
      key: 'job-applications',
      label: 'Front Door',
      Icon: JobApplicationsIcon,
      to: `/clients/${client.id}/opportunities`,
      count: counts['job-applications'],
      // Shown disabled rather than hidden: the board is part of the service
      // this client bought, and hiding it reads as "this client doesn't have
      // one" instead of "not yet".
      disabledReason: isJobSearchEligible(client)
        ? undefined
        : 'Opens once this client is market ready',
    },
    {
      key: 'networking',
      label: 'Side Door',
      Icon: NetworkingIcon,
      to: `/clients/${client.id}/networking`,
      count: counts.networking,
    },
    {
      key: 'email',
      label: 'Email',
      Icon: EmailIcon,
      // clientId rides along so the inbox can point back at this record; the
      // filtering itself still runs off the search term (MWA-335).
      to: `/emails?search=${encodeURIComponent(emailSearch)}&clientId=${client.id}`,
      count: counts.email,
    },
    {
      key: 'sms',
      label: 'SMS',
      Icon: SmsIcon,
      to: `/messages?clientId=${client.id}`,
      count: counts.sms,
    },
    {
      key: 'north-star',
      label: 'North Star',
      Icon: NorthStarIcon,
      to: `/clients/${client.id}/north-star`,
    },
  ]

  return (
    <Box component="nav" aria-label="Related records">
      <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
        {entries.map(({ key, label, Icon, to, count, disabledReason }) => (
          <Tooltip key={key} title={disabledReason || ''}>
            {/* A disabled button fires no events, so the tooltip needs an
                enabled element of its own to listen on. */}
            <span>
              <Button
                size="small"
                disabled={!!disabledReason}
                onClick={() => navigate(to)}
                startIcon={<Icon fontSize="small" />}
                aria-label={
                  count === undefined ? label : `${label}, ${count} record${count === 1 ? '' : 's'}`
                }
                sx={{ color: 'text.secondary', px: 1.25 }}
              >
                {label}
                {count !== undefined && (
                  <Typography
                    component="span"
                    variant="caption"
                    aria-hidden="true"
                    sx={{ ml: 0.75, fontWeight: 700 }}
                  >
                    ({count})
                  </Typography>
                )}
              </Button>
            </span>
          </Tooltip>
        ))}
      </Stack>
    </Box>
  )
}
